import { genMsg } from '@ps-smart-cp/ppfw-client';
import type { AttachmentDraft, ChatAttachment } from '../models/attachment';

/** server（AgentAttachmentPolicy）/ agent 側の制限と同値 */
export const MAX_ATTACHMENTS = 4;
const MAX_IMAGE_SIZE_BYTES = 3_670_016;
const MAX_DOCUMENT_SIZE_BYTES = 4_718_592;
const BYTES_PER_MEGABYTE = 1024 * 1024;

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
const DOCUMENT_MIME_TYPES = ['application/pdf'] as const;
const ALLOWED_MIME_TYPES: readonly string[] = [...IMAGE_MIME_TYPES, ...DOCUMENT_MIME_TYPES];

/** input[type=file] の accept 属性。任意 binary を開かないため wildcard は使わない */
export const ATTACHMENT_ACCEPT_ATTRIBUTE = ALLOWED_MIME_TYPES.join(',');

/** MIME 原文は業務ユーザーに見せない */
const TYPE_LABELS: Record<string, string> = {
  'image/jpeg': '画像',
  'image/png': '画像',
  'image/webp': '画像',
  'application/pdf': 'PDF ファイル',
};

const FILE_FORMAT_ITEM_NAME = 'ファイル形式';
const ATTACHMENT_COUNT_ITEM_NAME = '添付ファイル件数';

export interface AttachmentRejection {
  reason: 'unsupported_type' | 'too_large' | 'too_many';
  file: File;
}

/** framework message（efw12013 / efw54003 / efw12009）を hook 層から受け取る */
export interface AttachmentRejectionMessages {
  unsupportedType: string;
  tooLarge: string;
  tooMany: string;
}

export function isImageMimeType(mimeType: string): boolean {
  return (IMAGE_MIME_TYPES as readonly string[]).includes(mimeType);
}

function maxSizeBytesFor(mimeType: string): number {
  return isImageMimeType(mimeType) ? MAX_IMAGE_SIZE_BYTES : MAX_DOCUMENT_SIZE_BYTES;
}

export function attachmentTypeLabel(mimeType: string): string {
  return TYPE_LABELS[mimeType] ?? 'ファイル';
}

/** server の toMegaBytes（切り上げ小数1桁）と同じ丸め */
function toMegaBytes(bytes: number): string {
  return (Math.ceil((bytes / BYTES_PER_MEGABYTE) * 10) / 10).toFixed(1);
}

function findRejectionReason(file: File, currentCount: number): AttachmentRejection['reason'] | null {
  if (currentCount >= MAX_ATTACHMENTS) {
    return 'too_many';
  }
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return 'unsupported_type';
  }
  if (file.size > maxSizeBytesFor(file.type)) {
    return 'too_large';
  }
  return null;
}

/** 上限を跨ぐ選択でも収まる分は受け入れる。画像と PDF は同じ枠を共有する。 */
export function partitionSelectedAttachments(
  files: readonly File[],
  currentCount: number,
): { accepted: File[]; rejection: AttachmentRejection | null } {
  const accepted: File[] = [];
  let rejection: AttachmentRejection | null = null;
  for (const file of files) {
    const reason = findRejectionReason(file, currentCount + accepted.length);
    if (reason) {
      rejection ??= { reason, file };
      continue;
    }
    accepted.push(file);
  }
  return { accepted, rejection };
}

export function attachmentRejectionMessage(
  rejection: AttachmentRejection,
  messages: AttachmentRejectionMessages,
): string {
  const { reason, file } = rejection;
  if (reason === 'too_many') {
    return genMsg(messages.tooMany, ATTACHMENT_COUNT_ITEM_NAME, String(MAX_ATTACHMENTS));
  }
  if (reason === 'too_large') {
    return genMsg(
      messages.tooLarge,
      file.name,
      toMegaBytes(file.size),
      toMegaBytes(maxSizeBytesFor(file.type)),
    );
  }
  return genMsg(messages.unsupportedType, FILE_FORMAT_ITEM_NAME);
}

export function toChatAttachments(drafts: readonly AttachmentDraft[]): ChatAttachment[] {
  return drafts
    .filter((draft): draft is AttachmentDraft & { key: string } => Boolean(draft.key))
    .map(({ key, fileName, mimeType }) => ({ key, fileName, mimeType }));
}

export function hasUploadingAttachment(drafts: readonly AttachmentDraft[]): boolean {
  return drafts.some((draft) => draft.status === 'uploading');
}
